import DownloadIcon from "@mui/icons-material/Download"
import {
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useTheme,
} from "@mui/material"
import { ReactElement } from "react"

import type {
    TAmendmentChange,
    TAmendmentDifferenceValue,
    TAmendmentSection,
    TDifferencesDisplayProps,
} from "./amendmentHistoryTypes"
import { formatDifferenceValue } from "./amendmentHistoryUtils"
import useDownload from "../../../services/middleware/hooks/useDownload"
import { DNO_ATTACHMENT } from "../../../services/middleware/urls/hybrid"
import AppTooltip from "../../helper/appTooltip"

/**
 * Icon-only download button for an attachment value.
 */
const AttachmentDownload = ({
    fileUrl,
    applicationId,
    label,
    disabled = false,
    subdued = false,
}: {
    fileUrl: string
    applicationId: string
    label: string
    disabled?: boolean
    subdued?: boolean
}): ReactElement => {
    const { downloadAttachment, isLoading } = useDownload()

    const handleDownload = (): void => {
        downloadAttachment(DNO_ATTACHMENT(applicationId, fileUrl))
    }

    return (
        <AppTooltip
            title={
                disabled
                    ? "This file is no longer available"
                    : `Download ${label}`
            }
        >
            <span>
                <IconButton
                    size="small"
                    onClick={handleDownload}
                    disabled={disabled || isLoading}
                    color="primary"
                    aria-label={`Download ${label}`}
                    sx={subdued ? { opacity: 0.75 } : undefined}
                >
                    <DownloadIcon fontSize="small" />
                </IconButton>
            </span>
        </AppTooltip>
    )
}

AttachmentDownload.defaultProps = {
    disabled: false,
    subdued: false,
}

/**
 * Extracts the file URL string from an attachment value,
 * whether it is a raw string or an object with a fileUrl
 * key.
 */
const extractFileUrl = (value: TAmendmentDifferenceValue): string | null => {
    if (typeof value === "string") return value
    if (typeof value === "object" && value !== null) {
        const obj = value as { fileUrl?: unknown }
        if (typeof obj.fileUrl === "string") return obj.fileUrl
    }
    return null
}

/**
 * Renders an attachment download icon or formatted text
 * value.
 */
const renderChangeValue = (
    value: TAmendmentDifferenceValue,
    change: TAmendmentChange,
    applicationId: string,
    label: string,
    isRedacted?: boolean,
    isPrevious?: boolean
): ReactElement | string => {
    if (change.isAttachment) {
        const url = extractFileUrl(value)
        if (url) {
            return (
                <AttachmentDownload
                    fileUrl={url}
                    applicationId={applicationId}
                    label={label}
                    disabled={isRedacted}
                    subdued={isPrevious}
                />
            )
        }
    }
    return formatDifferenceValue(value)
}

/**
 * Displays section-grouped differences for a single amendment
 * entry as a four-column table: Section, Field, Previous, and
 * Latest.  Section groups are separated by a thick top border.
 */
const DifferencesDisplay = ({
    differences,
    applicationId,
    isRedacted,
}: TDifferencesDisplayProps): ReactElement | null => {
    const theme = useTheme()

    if (!Array.isArray(differences) || differences.length === 0) {
        return null
    }

    const isLegacyFormat =
        differences.length > 0 &&
        "field" in differences[0] &&
        !("section" in differences[0])

    const sections: TAmendmentSection[] = isLegacyFormat
        ? [
              {
                  section: "Changes",
                  changes: differences as unknown as TAmendmentChange[],
              },
          ]
        : differences

    return (
        <TableContainer>
            <Table size="small" sx={{ tableLayout: "fixed" }}>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ width: "20%" }}>
                            <Typography
                                variant="body2"
                                fontWeight="medium"
                                color="primary"
                            >
                                Section
                            </Typography>
                        </TableCell>
                        <TableCell sx={{ width: "22%" }}>
                            <Typography
                                variant="body2"
                                fontWeight="medium"
                                color="primary"
                            >
                                Field
                            </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ width: "29%" }}>
                            <Typography
                                variant="body2"
                                fontWeight="medium"
                                color="primary"
                            >
                                Previous
                            </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ width: "29%" }}>
                            <Typography
                                variant="body2"
                                fontWeight="medium"
                                color="primary"
                            >
                                Latest
                            </Typography>
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sections.map((section, sIdx) => {
                        const changes = section.changes ?? []
                        return changes.map((change, cIdx) => (
                            <TableRow
                                // API returns sections/changes in fixed order with no stable ID
                                // eslint-disable-next-line react/no-array-index-key
                                key={`${sIdx}-${cIdx}`}
                                hover
                                sx={{
                                    ...(cIdx === 0 && sIdx > 0
                                        ? {
                                              "& td": {
                                                  borderTop: `2px solid ${theme.palette.divider}`,
                                              },
                                          }
                                        : {}),
                                    "&:last-child td": {
                                        borderBottom: "none",
                                    },
                                }}
                            >
                                <TableCell
                                    sx={{
                                        py: 0.75,
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        fontWeight="600"
                                        color="text.secondary"
                                    >
                                        {section.section}
                                    </Typography>
                                </TableCell>
                                <TableCell
                                    sx={{
                                        py: 0.75,
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        fontWeight="500"
                                    >
                                        {change.field}
                                    </Typography>
                                </TableCell>
                                <TableCell
                                    align="center"
                                    sx={{
                                        py: 0.75,
                                        color: theme.palette.text.secondary,
                                    }}
                                >
                                    {renderChangeValue(
                                        change.previousValue,
                                        change,
                                        applicationId,
                                        `${change.field} Previous`,
                                        isRedacted,
                                        true
                                    )}
                                </TableCell>
                                <TableCell
                                    align="center"
                                    sx={{
                                        py: 0.75,
                                    }}
                                >
                                    {renderChangeValue(
                                        change.latestValue,
                                        change,
                                        applicationId,
                                        `${change.field} Latest`,
                                        isRedacted,
                                        false
                                    )}
                                </TableCell>
                            </TableRow>
                        ))
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    )
}

export default DifferencesDisplay
